export interface StudioTarget {
  screen?: string;
  appId?: number;
  collection?: string;
  view?: 'catalog' | 'tools';
  options?: Record<string, unknown>;
  ret?: boolean;
}
const key = 'steos:studio:target';
const entryKeys = [
  'who',
  'drive',
  'words',
  'fmt',
  'char',
  'card',
  'mode',
  'sty',
  'preset',
  'tool',
  'shoot',
  'out',
  'noise',
  'voicePick',
  'open_role',
];
export function studioTarget(value: unknown): StudioTarget | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  // Старый шаблон передаёт эти входы из меню, результатов и подборок.
  // Нормализуем маршрут, сохраняя серверную проверку appId и права владельца.
  const target = { ...raw } as StudioTarget;
  if (target.appId === undefined && raw.app_id !== undefined) target.appId = raw.app_id as number;
  if (target.collection === undefined && (typeof raw.coll === 'string' || typeof raw.coll === 'number'))
    target.collection = String(raw.coll);
  if (raw.catalog === true && !target.view) target.view = 'catalog';
  if ((raw.tools === true || typeof raw.tools === 'string') && !target.view) target.view = 'tools';
  if (target.screen === 'market') {
    target.screen = undefined;
    target.collection ??= 'market';
  }
  if (
    !target.screen &&
    !target.appId &&
    !target.collection &&
    !target.view &&
    (Object.keys(raw).length === 0 || ['who', 'char', 'fmt', 'drive', 'src'].some(key => Object.hasOwn(raw, key)))
  )
    target.screen = raw.drive === 'video' ? 'dance' : 'circle';
  if (target.appId !== undefined && (!Number.isSafeInteger(target.appId) || target.appId < 1)) return null;
  if (target.screen !== undefined && !/^[a-z][a-z0-9_]{0,31}$/.test(target.screen)) return null;
  if (target.collection !== undefined && !/^[a-z0-9_-]{1,64}$/.test(target.collection)) return null;
  if (!(target.appId || target.screen || target.collection || ['catalog', 'tools'].includes(target.view ?? ''))) return null;
  const options: Record<string, string> = {};
  const supplied = target.options && typeof target.options === 'object' && !Array.isArray(target.options) ? target.options : {};
  for (const [name, option] of [
    ...entryKeys.filter(name => Object.hasOwn(raw, name)).map(name => [name, raw[name]] as const),
    ...Object.entries(supplied),
  ]) {
    if (/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(name) && name !== 'consent_accepted' && ['string', 'boolean', 'number'].includes(typeof option))
      options[name] = String(option);
  }
  if (typeof raw.tools === 'string') options.section = raw.tools;
  if (target.screen === 'dance' && raw.drive === 'video') options.preset = 'swap';
  // Только скалярные настройки. Объекты demo-файлов нельзя превращать в реальные uploads.
  if (options.fmt && !options.format) options.format = options.fmt;
  if (options.who && !options.source) options.source = ['video', 'audio'].includes(options.who) ? options.who : 'face';
  if (options.char && /^[1-9][0-9]*$/.test(options.char) && !options.cast) options.cast = `character:${options.char}`;
  return {
    screen: target.screen,
    appId: target.appId,
    collection: target.collection,
    view: target.view,
    ...(raw.ret === true ? { ret: true } : {}),
    options: Object.keys(options).length ? options : undefined,
  };
}
export function openStudioTarget(value: unknown) {
  const target = studioTarget(value);
  if (!target) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(target));
  } catch {
    /* Текущий экран всё равно получает событие. */
  }
  document.dispatchEvent(new CustomEvent('steos:studio:target', { detail: target }));
}
export function readStudioTarget(): StudioTarget | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) || 'null');
    return studioTarget(value);
  } catch {
    return null;
  }
}
export function consumeStudioTarget(expected?: StudioTarget): StudioTarget | null {
  const target = readStudioTarget();
  if (expected && JSON.stringify(target) !== JSON.stringify(expected)) return null;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* Событие всё равно доставлено текущей форме. */
  }
  return target;
}
